import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { useLanguage } from '../../context/LanguageContext';

const API = '/api/v1/fsm';

const Technicians = () => {
  const { lang, t } = useLanguage();
  const f = t('fsm') || {};
  const isRTL = lang === 'ar';
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [technicians, setTechnicians] = useState([]);
  const [categories, setCategories] = useState([]);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editTech, setEditTech] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [unavailForm, setUnavailForm] = useState(null);

  const emptyForm = {
    agent_id: '', hourly_rate: '', max_daily_work_orders: 6,
    home_address: '', color: '#3B82F6', notes: '', skills: []
  };
  const [form, setForm] = useState({ ...emptyForm });

  const colors = ['#3B82F6','#10B981','#F59E0B','#EF4444','#8B5CF6','#EC4899','#06B6D4','#F97316'];

  const fetchTechnicians = useCallback(async () => {
    setLoading(true);
    try {
      const url = search ? `${API}/technicians?search=${encodeURIComponent(search)}` : `${API}/technicians`;
      const res = await fetch(url, { headers });
      const data = await res.json();
      if (data.success) setTechnicians(data.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [search]);

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API}/equipment-categories`, { headers });
      const data = await res.json();
      if (data.success) setCategories(data.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchAvailableAgents = async () => {
    try {
      const res = await fetch(`${API}/technicians/agents-available`, { headers });
      const data = await res.json();
      if (data.success) setAvailableAgents(data.data || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchTechnicians(); fetchCategories(); }, [fetchTechnicians]);

  const openCreate = () => {
    fetchAvailableAgents();
    setEditTech(null);
    setForm({ ...emptyForm });
    setShowForm(true);
  };

  const openEdit = (tech) => {
    fetchAvailableAgents();
    setEditTech(tech);
    setForm({
      agent_id: tech.agent_id,
      hourly_rate: tech.hourly_rate || '',
      max_daily_work_orders: tech.max_daily_work_orders || 6,
      home_address: tech.home_address || '',
      color: tech.color || '#3B82F6',
      notes: tech.notes || '',
      skills: []
    });
    // Load current skills
    fetch(`${API}/technicians/${tech.id}`, { headers })
      .then(r => r.json())
      .then(d => {
        if (d.success && d.data.skills) {
          setForm(prev => ({ ...prev, skills: d.data.skills.map(s => ({ category_id: s.category_id, skill_level: s.skill_level })) }));
        }
      });
    setShowForm(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editTech) {
        // Update technician profile
        await fetch(`${API}/technicians/${editTech.id}`, {
          method: 'PUT', headers,
          body: JSON.stringify({
            hourly_rate: form.hourly_rate || null,
            max_daily_work_orders: form.max_daily_work_orders,
            home_address: form.home_address || null,
            color: form.color, notes: form.notes || null
          })
        });
        // Update skills
        await fetch(`${API}/technicians/${editTech.id}/skills`, {
          method: 'PUT', headers,
          body: JSON.stringify({ skills: form.skills })
        });
      } else {
        if (!form.agent_id) { alert('Please select an agent'); setSaving(false); return; }
        await fetch(`${API}/technicians`, {
          method: 'POST', headers,
          body: JSON.stringify(form)
        });
      }
      setShowForm(false);
      fetchTechnicians();
      fetchAvailableAgents();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(isRTL ? 'هل تريد إلغاء تفعيل هذا الفني؟' : 'Deactivate this technician?')) return;
    await fetch(`${API}/technicians/${id}`, { method: 'DELETE', headers });
    fetchTechnicians();
  };

  const toggleSkill = (catId) => {
    setForm(prev => {
      const exists = prev.skills.find(s => s.category_id === catId);
      if (exists) return { ...prev, skills: prev.skills.filter(s => s.category_id !== catId) };
      return { ...prev, skills: [...prev.skills, { category_id: catId, skill_level: 'mid' }] };
    });
  };

  const setSkillLevel = (catId, level) => {
    setForm(prev => ({
      ...prev,
      skills: prev.skills.map(s => s.category_id === catId ? { ...s, skill_level: level } : s)
    }));
  };

  const openDetail = async (tech) => {
    try {
      const res = await fetch(`${API}/technicians/${tech.id}`, { headers });
      const data = await res.json();
      if (data.success) setShowDetail(data.data);
    } catch (e) { console.error(e); }
  };

  const handleAddUnavail = async () => {
    if (!unavailForm) return;
    try {
      await fetch(`${API}/technicians/${unavailForm.technician_id}/unavailability`, {
        method: 'POST', headers,
        body: JSON.stringify(unavailForm)
      });
      setUnavailForm(null);
      if (showDetail) openDetail(showDetail);
    } catch (e) { console.error(e); }
  };

  const handleDeleteUnavail = async (unavailId) => {
    await fetch(`${API}/technicians/unavailability/${unavailId}`, { method: 'DELETE', headers });
    if (showDetail) openDetail(showDetail);
  };

  // ── Styles ──
  const s = {
    card: { background: '#1e293b', borderRadius: 12, border: '1px solid rgba(255,255,255,0.08)', padding: 20, marginBottom: 16 },
    input: { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)', background: '#0f172a', color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box' },
    label: { display: 'block', color: '#94a3b8', fontSize: 13, marginBottom: 6, fontWeight: 500 },
    btn: (bg) => ({ padding: '10px 20px', borderRadius: 8, border: 'none', background: bg || '#3b82f6', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14 }),
    badge: (bg) => ({ display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: bg || '#334155', color: '#fff', marginRight: 6, marginBottom: 4 }),
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
    modal: { background: '#1e293b', borderRadius: 16, padding: 28, width: '90%', maxWidth: 600, maxHeight: '85vh', overflowY: 'auto', border: '1px solid rgba(255,255,255,0.1)' },
    skillChip: (active) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 20, border: active ? '2px solid #3b82f6' : '1px solid rgba(255,255,255,0.15)', background: active ? 'rgba(59,130,246,0.15)' : 'transparent', color: active ? '#93c5fd' : '#94a3b8', cursor: 'pointer', fontSize: 13, marginRight: 8, marginBottom: 8, transition: 'all 0.2s' }),
    levelSelect: { padding: '3px 8px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.2)', background: '#0f172a', color: '#fff', fontSize: 12 },
    th: { padding: '12px 16px', textAlign: isRTL ? 'right' : 'left', color: '#64748b', fontWeight: 600, fontSize: 12, textTransform: 'uppercase', borderBottom: '1px solid rgba(255,255,255,0.08)' },
    td: { padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)', color: '#e2e8f0', fontSize: 14 },
  };

  const skillLevels = [
    { value: 'junior', label: isRTL ? 'مبتدئ' : 'Junior', color: '#94a3b8' },
    { value: 'mid', label: isRTL ? 'متوسط' : 'Mid', color: '#3b82f6' },
    { value: 'senior', label: isRTL ? 'خبير' : 'Senior', color: '#f59e0b' },
    { value: 'expert', label: isRTL ? 'متخصص' : 'Expert', color: '#10b981' },
  ];

  return (
    <Layout>
      <div style={{ direction: isRTL ? 'rtl' : 'ltr', padding: '20px 24px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h1 style={{ color: '#f1f5f9', fontSize: 24, fontWeight: 700, margin: 0 }}>
            {isRTL ? '👷 إدارة الفنيين' : '👷 Technicians'}
          </h1>
          <button onClick={openCreate} style={s.btn('#3b82f6')}>
            {isRTL ? '+ إضافة فني' : '+ Add Technician'}
          </button>
        </div>

        {/* Search */}
        <div style={{ marginBottom: 20 }}>
          <input
            placeholder={isRTL ? 'بحث بالاسم أو البريد...' : 'Search by name or email...'}
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ ...s.input, maxWidth: 400 }}
          />
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Loading...</div>
        ) : technicians.length === 0 ? (
          <div style={{ ...s.card, textAlign: 'center', padding: 60, color: '#64748b' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>👷</div>
            <div style={{ fontSize: 16 }}>{isRTL ? 'لا يوجد فنيين. أضف أول فني.' : 'No technicians yet. Add your first technician.'}</div>
          </div>
        ) : (
          <div style={{ ...s.card, padding: 0, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#0f172a' }}>
                  <th style={s.th}></th>
                  <th style={s.th}>{isRTL ? 'الاسم' : 'Name'}</th>
                  <th style={s.th}>{isRTL ? 'المهارات' : 'Skills'}</th>
                  <th style={s.th}>{isRTL ? 'الأجر/ساعة' : 'Rate/hr'}</th>
                  <th style={s.th}>{isRTL ? 'أوامر اليوم' : 'Today'}</th>
                  <th style={s.th}>{isRTL ? 'نشطة' : 'Active WOs'}</th>
                  <th style={s.th}>{isRTL ? 'إجراءات' : 'Actions'}</th>
                </tr>
              </thead>
              <tbody>
                {technicians.map(tech => (
                  <tr key={tech.id} style={{ cursor: 'pointer' }} onClick={() => openDetail(tech)}>
                    <td style={s.td}>
                      <div style={{ width: 12, height: 12, borderRadius: '50%', background: tech.color || '#666' }} />
                    </td>
                    <td style={s.td}>
                      <div style={{ fontWeight: 600 }}>{tech.full_name}</div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>{tech.phone}</div>
                    </td>
                    <td style={s.td}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {(isRTL ? tech.skill_names_ar : tech.skill_names)?.split(', ').filter(Boolean).map((sk, i) => (
                          <span key={i} style={s.badge('#334155')}>{sk}</span>
                        ))}
                      </div>
                    </td>
                    <td style={s.td}>{tech.hourly_rate ? `${tech.hourly_rate}` : '-'}</td>
                    <td style={s.td}>
                      <span style={{ fontWeight: 700, color: tech.today_wo_count >= tech.max_daily_work_orders ? '#ef4444' : '#10b981' }}>
                        {tech.today_wo_count}/{tech.max_daily_work_orders}
                      </span>
                    </td>
                    <td style={s.td}>{tech.active_wo_count}</td>
                    <td style={s.td} onClick={e => e.stopPropagation()}>
                      <button onClick={() => openEdit(tech)} style={{ ...s.btn('#334155'), padding: '6px 12px', fontSize: 12, marginRight: 6 }}>
                        {isRTL ? 'تعديل' : 'Edit'}
                      </button>
                      <button onClick={() => handleDelete(tech.id)} style={{ ...s.btn('#7f1d1d'), padding: '6px 12px', fontSize: 12 }}>
                        {isRTL ? 'تعطيل' : 'Deactivate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create/Edit Modal */}
        {showForm && (
          <div style={s.overlay} onClick={() => setShowForm(false)}>
            <div style={s.modal} onClick={e => e.stopPropagation()}>
              <h2 style={{ color: '#f1f5f9', margin: '0 0 24px', fontSize: 20 }}>
                {editTech ? (isRTL ? 'تعديل فني' : 'Edit Technician') : (isRTL ? 'إضافة فني جديد' : 'Add Technician')}
              </h2>

              {!editTech && (
                <div style={{ marginBottom: 16 }}>
                  <label style={s.label}>{isRTL ? 'اختر الموظف' : 'Select Agent'} *</label>
                  <select value={form.agent_id} onChange={e => setForm(p => ({ ...p, agent_id: parseInt(e.target.value) || '' }))} style={s.input}>
                    <option value="">{isRTL ? '-- اختر --' : '-- Select --'}</option>
                    {availableAgents.map(a => (
                      <option key={a.id} value={a.id}>{a.full_name} ({a.email})</option>
                    ))}
                  </select>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={s.label}>{isRTL ? 'الأجر بالساعة' : 'Hourly Rate'}</label>
                  <input type="number" value={form.hourly_rate} onChange={e => setForm(p => ({ ...p, hourly_rate: e.target.value }))} style={s.input} placeholder="0.00" />
                </div>
                <div>
                  <label style={s.label}>{isRTL ? 'أقصى أوامر يومية' : 'Max Daily WOs'}</label>
                  <input type="number" value={form.max_daily_work_orders} onChange={e => setForm(p => ({ ...p, max_daily_work_orders: parseInt(e.target.value) || 6 }))} style={s.input} />
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={s.label}>{isRTL ? 'العنوان' : 'Home Address'}</label>
                <input value={form.home_address} onChange={e => setForm(p => ({ ...p, home_address: e.target.value }))} style={s.input} />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={s.label}>{isRTL ? 'اللون (لوحة التوزيع)' : 'Color (Dispatch Board)'}</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {colors.map(c => (
                    <div key={c} onClick={() => setForm(p => ({ ...p, color: c }))}
                      style={{ width: 32, height: 32, borderRadius: 8, background: c, cursor: 'pointer',
                        border: '2px solid transparent', outline: form.color === c ? '3px solid #fff' : 'none', outlineOffset: '3px', transition: 'all 0.2s' }} />
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={s.label}>{isRTL ? 'ملاحظات' : 'Notes'}</label>
                <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} style={{ ...s.input, minHeight: 60 }} />
              </div>

              {/* Skills */}
              <div style={{ marginBottom: 24 }}>
                <label style={s.label}>{isRTL ? 'المهارات (فئات المعدات)' : 'Skills (Equipment Categories)'}</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', marginTop: 8 }}>
                  {categories.filter(c => c.is_active).map(cat => {
                    const active = form.skills.find(s => s.category_id === cat.id);
                    return (
                      <div key={cat.id} style={s.skillChip(!!active)} onClick={() => toggleSkill(cat.id)}>
                        <span>{cat.icon || '🔧'}</span>
                        <span>{isRTL ? cat.name_ar || cat.name : cat.name}</span>
                        {active && (
                          <select value={active.skill_level} onClick={e => e.stopPropagation()}
                            onChange={e => setSkillLevel(cat.id, e.target.value)} style={s.levelSelect}>
                            {skillLevels.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                          </select>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <button onClick={() => setShowForm(false)} style={s.btn('#334155')}>{isRTL ? 'إلغاء' : 'Cancel'}</button>
                <button onClick={handleSave} disabled={saving} style={s.btn('#3b82f6')}>
                  {saving ? '...' : (editTech ? (isRTL ? 'حفظ' : 'Save') : (isRTL ? 'إضافة' : 'Add'))}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Detail Modal */}
        {showDetail && (
          <div style={s.overlay} onClick={() => setShowDetail(null)}>
            <div style={s.modal} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 16, height: 16, borderRadius: '50%', background: showDetail.color || '#666' }} />
                  <h2 style={{ color: '#f1f5f9', margin: 0, fontSize: 20 }}>{showDetail.full_name}</h2>
                </div>
                <button onClick={() => setShowDetail(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 24, cursor: 'pointer' }}>×</button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
                <div style={{ padding: 12, background: '#0f172a', borderRadius: 8 }}>
                  <div style={{ color: '#64748b', fontSize: 12 }}>{isRTL ? 'البريد' : 'Email'}</div>
                  <div style={{ color: '#e2e8f0', fontSize: 14, marginTop: 4 }}>{showDetail.email}</div>
                </div>
                <div style={{ padding: 12, background: '#0f172a', borderRadius: 8 }}>
                  <div style={{ color: '#64748b', fontSize: 12 }}>{isRTL ? 'الهاتف' : 'Phone'}</div>
                  <div style={{ color: '#e2e8f0', fontSize: 14, marginTop: 4 }}>{showDetail.phone}</div>
                </div>
                <div style={{ padding: 12, background: '#0f172a', borderRadius: 8 }}>
                  <div style={{ color: '#64748b', fontSize: 12 }}>{isRTL ? 'الأجر/ساعة' : 'Rate/hr'}</div>
                  <div style={{ color: '#e2e8f0', fontSize: 14, marginTop: 4 }}>{showDetail.hourly_rate || '-'}</div>
                </div>
              </div>

              {/* Skills */}
              <div style={{ marginBottom: 20 }}>
                <h3 style={{ color: '#94a3b8', fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{isRTL ? 'المهارات' : 'Skills'}</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {(showDetail.skills || []).map(sk => (
                    <div key={sk.id} style={{ ...s.badge('#1e3a5f'), display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px' }}>
                      <span>{sk.icon || '🔧'}</span>
                      <span>{isRTL ? sk.name_ar || sk.name : sk.name}</span>
                      <span style={{ ...s.badge(skillLevels.find(l => l.value === sk.skill_level)?.color || '#64748b'), fontSize: 10, marginRight: 0 }}>
                        {skillLevels.find(l => l.value === sk.skill_level)?.label || sk.skill_level}
                      </span>
                    </div>
                  ))}
                  {(!showDetail.skills || showDetail.skills.length === 0) && (
                    <span style={{ color: '#64748b', fontSize: 13 }}>{isRTL ? 'لا توجد مهارات' : 'No skills assigned'}</span>
                  )}
                </div>
              </div>

              {/* Unavailability */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <h3 style={{ color: '#94a3b8', fontSize: 14, fontWeight: 600, margin: 0 }}>{isRTL ? 'أوقات عدم التوفر' : 'Unavailability'}</h3>
                  <button onClick={() => setUnavailForm({ technician_id: showDetail.id, unavailability_type: 'leave', start_date: '', end_date: '', reason: '' })}
                    style={{ ...s.btn('#334155'), padding: '5px 12px', fontSize: 12 }}>
                    {isRTL ? '+ إضافة' : '+ Add'}
                  </button>
                </div>
                {(showDetail.unavailability || []).map(u => (
                  <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#0f172a', borderRadius: 8, marginBottom: 6 }}>
                    <div>
                      <span style={s.badge(u.unavailability_type === 'sick' ? '#7f1d1d' : '#334155')}>{u.unavailability_type}</span>
                      <span style={{ color: '#e2e8f0', fontSize: 13 }}>{u.start_date?.split('T')[0]} → {u.end_date?.split('T')[0]}</span>
                      {u.reason && <span style={{ color: '#64748b', fontSize: 12, marginLeft: 8 }}>({u.reason})</span>}
                    </div>
                    <button onClick={() => handleDeleteUnavail(u.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16 }}>×</button>
                  </div>
                ))}
                {(!showDetail.unavailability || showDetail.unavailability.length === 0) && (
                  <div style={{ color: '#64748b', fontSize: 13, padding: 8 }}>{isRTL ? 'متاح دائماً' : 'Always available'}</div>
                )}
              </div>

              {/* Unavailability Form */}
              {unavailForm && (
                <div style={{ padding: 16, background: '#0f172a', borderRadius: 12, marginBottom: 16 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                    <div>
                      <label style={s.label}>{isRTL ? 'النوع' : 'Type'}</label>
                      <select value={unavailForm.unavailability_type} onChange={e => setUnavailForm(p => ({ ...p, unavailability_type: e.target.value }))} style={s.input}>
                        <option value="leave">{isRTL ? 'إجازة' : 'Leave'}</option>
                        <option value="sick">{isRTL ? 'مرضية' : 'Sick'}</option>
                        <option value="training">{isRTL ? 'تدريب' : 'Training'}</option>
                        <option value="personal">{isRTL ? 'شخصي' : 'Personal'}</option>
                        <option value="blocked">{isRTL ? 'محجوب' : 'Blocked'}</option>
                      </select>
                    </div>
                    <div>
                      <label style={s.label}>{isRTL ? 'من' : 'From'}</label>
                      <input type="date" value={unavailForm.start_date} onChange={e => setUnavailForm(p => ({ ...p, start_date: e.target.value }))} style={s.input} />
                    </div>
                    <div>
                      <label style={s.label}>{isRTL ? 'إلى' : 'To'}</label>
                      <input type="date" value={unavailForm.end_date} onChange={e => setUnavailForm(p => ({ ...p, end_date: e.target.value }))} style={s.input} />
                    </div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={s.label}>{isRTL ? 'السبب' : 'Reason'}</label>
                    <input value={unavailForm.reason} onChange={e => setUnavailForm(p => ({ ...p, reason: e.target.value }))} style={s.input} />
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button onClick={() => setUnavailForm(null)} style={s.btn('#334155')}>{isRTL ? 'إلغاء' : 'Cancel'}</button>
                    <button onClick={handleAddUnavail} style={s.btn('#3b82f6')}>{isRTL ? 'حفظ' : 'Save'}</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Technicians;
